import React from 'react';
import PropTypes from 'prop-types';
import {observer} from 'mobx-react';
import * as R from 'ramda/src/index';
import classNames from 'classnames';
import _MetaSearchResults from './components/results';
import Filters from './components/filters';
import Loader from './components/results/Loader';
import withTranslations from 'hoc/withTranslations';
import EventTrigger from 'helpers/EventTrigger';
import {isRTL, isoCurrency} from 'helpers/stableValues';

const MetaSearchResults = observer(_MetaSearchResults);

function renderTab(isRTL, searchQuery) {
  const {arrAirportCode, depAirportCode, ibDateFormatted, obDateFormatted, isRoundtrip} = searchQuery;
  const roundTripTabText = `${depAirportCode} - ${arrAirportCode}: ${obDateFormatted} - ${ibDateFormatted}`;
  const onewayTabText = `${depAirportCode} - ${arrAirportCode}: ${obDateFormatted}`;
  const rtlRoundTripTabText = `${arrAirportCode} - ${depAirportCode}: ${ibDateFormatted} - ${obDateFormatted}`;
  const rtlOnewayTabText = `${arrAirportCode} - ${depAirportCode}: ${obDateFormatted}`;

  if (isRTL) {
    return (isRoundtrip ? rtlRoundTripTabText : rtlOnewayTabText);
  }

  return (isRoundtrip ? roundTripTabText : onewayTabText);
}

const ResultsTabs = ({store, translations}) => {
  const {searching} = translations;
  const {tabs, selectedIndex, changeTab, closeTab} = store;
  const selectedTab = tabs[selectedIndex];
  const show = !R.isNil(selectedTab) ? selectedTab.loader : false;
  const selectedTabNoResultsNoLoad = selectedTab && !selectedTab.filteredResults.length && show;
  const wrapperClasses = classNames({
    'wrapper': true,
    'fd-layout': selectedTabNoResultsNoLoad
  });
  const gridClasses = classNames('grid-item', 'px-2 px-sm-3', {
    'grid-item-lg-7-10': selectedTabNoResultsNoLoad
  });

  if (selectedIndex < 0) {
    return null;
  }
  const {searchQuery, resultsCounts, filtersStore, filteredResults, loadingResults} = selectedTab;
  const {isRoundtrip, url, arr, dep} = searchQuery;

  return (
    <div className={wrapperClasses} style={{position: 'relative'}}>
      <div className="recent-searches-tabs pb-2 pb-md-0">
        <div className="container">
          <div className="pt-xs-3">
            <ul className="nav nav-tabs d-none d-md-flex">
              {tabs.map((tab, index) => {
                const url = tab.searchQuery.url;
                return <li key={url} className={`nav-item ${selectedIndex === index ? 'active' : ''}`}>
                  <a
                    className="nav-link"
                    onClick={(e) => {
                      e.preventDefault();
                      changeTab(index);
                    }}
                    href=""
                  >
                    {tabs.length > 1 &&
                      <button
                        type="button" className="close close-tab" aria-label="Close"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          closeTab(index);
                        }}
                      >
                        <span aria-hidden="true">×</span>
                      </button>
                    }
                    {renderTab(isRTL, tab.searchQuery)}
                  </a>
                </li>;
              })}
            </ul>
            <select
              id="selectTabSearch"
              className="form-control d-block d-md-none"
              value={url}
              onChange={(e) => {
                changeTab(tabs.findIndex(({searchQuery}) => searchQuery.url === e.target.value));
              }}
            >
              {tabs.map((tab) => (
                <option key={tab.searchQuery.url} value={tab.searchQuery.url}>
                  {renderTab(isRTL, tab.searchQuery)}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
      <div className="container">
        <div className="grid" style={{position: 'relative'}}>
          {
            (resultsCounts.total > 0 && !show) &&
            <Filters
              key={url}
              isRoundtrip={isRoundtrip}
              store={filtersStore}
              resultsCounts={resultsCounts}
              currency={isoCurrency()}
              translations={translations}
            />
          }
          <div id="main" className={gridClasses}>
            {show && <Loader isRTL={isRTL} from={dep} to={arr} text={searching} />}
            {!show &&
              <MetaSearchResults
                results={filteredResults}
                translations={translations}
                searchQuery={searchQuery}
                resultsCounts={resultsCounts}
                sorting={filtersStore.sorting}
                setSortingType={filtersStore.setSortingType}
                loading={loadingResults}
              />
            }
          </div>
          {
            (resultsCounts.total > 0 && !show) &&
              <EventTrigger id="results-ad" event="resultsAdRendered"/>
          }
        </div>
      </div>
    </div>
  );
};

ResultsTabs.propTypes = {
  store: PropTypes.object.isRequired,
  stableValues: PropTypes.object.isRequired,
  translations: PropTypes.object.isRequired
};

const ResultsTabsWithStableValuesAndTranslations = R.compose(
  withTranslations('resultsTranslations'),
  observer
)(ResultsTabs);

export default ResultsTabsWithStableValuesAndTranslations;
